import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from "@/components/ui/label";
import { WheelPicker, WheelPickerWrapper } from '@ncdai/react-wheel-picker';
import { useDeviceType } from '@/hooks/useDeviceType';

interface WheelPickerOption {
  label: string;
  value: string;
}

interface ProfileDateWheelPickerProps {
  id: string;
  label: string;
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  error?: string;
}

export const ProfileDateWheelPicker = ({
  id,
  label,
  value,
  onChange,
  error
}: ProfileDateWheelPickerProps) => {
  const { t } = useTranslation('common');
  const { isDesktop } = useDeviceType();
  
  // Convert Date to individual components with stable defaults
  const selectedYear = value ? value.getFullYear().toString() : '1995';
  const selectedMonth = value ? (value.getMonth() + 1).toString() : '1';
  const selectedDay = value ? value.getDate().toString() : '1';

  // Memoize options to prevent unnecessary regeneration
  const yearOptions = useMemo((): WheelPickerOption[] => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
    return years.map(year => ({ value: year.toString(), label: year.toString() }));
  }, []);

  const monthOptions = useMemo((): WheelPickerOption[] => {
    return [
      { value: '1', label: t('months.january', 'Januar') },
      { value: '2', label: t('months.february', 'Februar') },
      { value: '3', label: t('months.march', 'März') },
      { value: '4', label: t('months.april', 'April') },
      { value: '5', label: t('months.may', 'Mai') },
      { value: '6', label: t('months.june', 'Juni') },
      { value: '7', label: t('months.july', 'Juli') },
      { value: '8', label: t('months.august', 'August') },
      { value: '9', label: t('months.september', 'September') },
      { value: '10', label: t('months.october', 'Oktober') },
      { value: '11', label: t('months.november', 'November') },
      { value: '12', label: t('months.december', 'Dezember') },
    ];
  }, [t]);

  const dayOptions = useMemo((): WheelPickerOption[] => {
    const getDaysInMonth = (year: number, month: number) => {
      return new Date(year, month, 0).getDate();
    };

    const days = selectedYear && selectedMonth 
      ? Array.from({ length: getDaysInMonth(parseInt(selectedYear), parseInt(selectedMonth)) }, (_, i) => i + 1)
      : Array.from({ length: 31 }, (_, i) => i + 1);

    return days.map(day => ({ value: day.toString(), label: day.toString() }));
  }, [selectedYear, selectedMonth]);

  // Stabilize wheel picker props
  const wheelPickerProps = useMemo(() => ({
    dragSensitivity: isDesktop ? 6 : 3,
    visibleCount: isDesktop ? 12 : 16,
    optionItemHeight: 30,
    infinite: false,
  }), [isDesktop]);

  const handleDateChange = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      onChange(date);
    }
  };

  const handleYearChange = (year: string) => {
    handleDateChange(year, selectedMonth, selectedDay);
  };

  const handleMonthChange = (month: string) => {
    handleDateChange(selectedYear, month, selectedDay);
  };

  const handleDayChange = (day: string) => {
    handleDateChange(selectedYear, selectedMonth, day);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      
      <div className={`w-full rounded-lg border ${error ? 'border-red-500' : 'border-zinc-200'} bg-white px-1 shadow-xs`}>
        <WheelPickerWrapper>
          <WheelPicker
            options={yearOptions}
            value={selectedYear}
            onValueChange={handleYearChange}
            {...wheelPickerProps}
          />
          <WheelPicker
            options={monthOptions}
            value={selectedMonth}
            onValueChange={handleMonthChange}
            {...wheelPickerProps}
          />
          <WheelPicker
            options={dayOptions}
            value={selectedDay}
            onValueChange={handleDayChange}
            {...wheelPickerProps}
          />
        </WheelPickerWrapper>
      </div>

      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
};